import maplibregl from "maplibre-gl";

import "./style.css";
import { getLiveGauges, getLiveGaugesCached, getSnapshotAt, getRiverPolygons, DisplayType, SNAPSHOT_CACHE_KEY, type GaugeFeature, type GaugeFeatureCollection, type SnapshotAtResult } from "./api.ts";
import { updateAlertPanel } from "./components/alert-panel.ts";
import type * as ChartsModule from "./components/charts.ts";
import type { WellTimeRange } from "./components/well-chart.ts";
import { createLogger } from "./lib/logger.ts";
import { nzstHHmm, nzstNowForFlatpickr } from "./lib/nzst.ts";
import { loadIcons, RAINFALL_DRY_ICON, RAINFALL_LIGHT_ICON, RAINFALL_MODERATE_ICON, RAINFALL_HEAVY_ICON } from "./map/icons.ts";
import { addRainfallLayer, addRiverLayer, addRiverPolygonsLayer, addWellLayer, addFloodExtentsLayer } from "./map/layers.ts";
import { ALERT_COLORS, WELL_COLORS } from "./map/map-colors.ts";
import { addMultiGaugeMarkers, removeMultiGaugeMarkers, setMultiMarkersVisible } from "./map/multi-gauge-markers.ts";
import { colorRiverPolygons } from "./map/river-segments.ts";
import { setSelectedGauge } from "./map/selected-marker.ts";
import { DARK_STYLE_URL, OSM_RASTER_STYLE, HB_CENTER, HB_ZOOM, setupTerrain } from "./map/style.ts";

// chart.js, luxon, chartjs-adapter-luxon, and flatpickr are deferred — loaded only
// after the first gauge click so they don't block the initial map render
let chartsModule: typeof ChartsModule | null = null;
function loadCharts(): Promise<typeof ChartsModule> {
  if (chartsModule) return Promise.resolve(chartsModule);
  return import("./components/charts.ts").then((m) => { chartsModule = m; return m; });
}

const log = createLogger("Main");

// ── Map init ──────────────────────────────────────────────────────────────────

let usingFallback = false;

const map = new maplibregl.Map({
  container: "map",
  style: DARK_STYLE_URL,
  center: HB_CENTER,
  zoom: HB_ZOOM,
  pitch: 45,
  attributionControl: { compact: true },
});

// Fall back to OSM raster if Stadia fails (no API key for production domain).
// Terrain is not available with the raster fallback.
map.on("error", (e) => {
  if ((e as unknown as { error?: { status?: number } }).error?.status === 401) {
    usingFallback = true;
    map.setStyle(OSM_RASTER_STYLE);
  }
});

// ── Rainfall toggle ───────────────────────────────────────────────────────────

const rainfallToggleBtn = document.getElementById("rainfall-toggle")!;
const legendRainfall = document.getElementById("legend-rainfall")!;
let rainfallVisible = true;

// Populate rainfall legend with actual icons
legendRainfall.innerHTML =
  `<h3>Rainfall (mm/hr)</h3>` +
  ([
    [RAINFALL_DRY_ICON,      "Dry"],
    [RAINFALL_LIGHT_ICON,    "Light (1–10)"],
    [RAINFALL_MODERATE_ICON, "Moderate (10–25)"],
    [RAINFALL_HEAVY_ICON,    "Heavy (≥25)"],
  ] as [string, string][]).map(([svg, label]) =>
    `<div class="legend-item">` +
    `<img class="legend-icon" src="data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}">` +
    `<span>${label}</span></div>`,
  ).join("");

rainfallToggleBtn.addEventListener("click", () => {
  rainfallVisible = !rainfallVisible;
  const viz = rainfallVisible ? "visible" : "none";
  if (map.getLayer("rainfall-symbols")) map.setLayoutProperty("rainfall-symbols", "visibility", viz);
  if (map.getLayer("rainfall-trends")) map.setLayoutProperty("rainfall-trends", "visibility", viz);
  setMultiMarkersVisible(rainfallVisible, riverVisible);
  rainfallToggleBtn.classList.toggle("active", rainfallVisible);
  legendRainfall.classList.toggle("visible", rainfallVisible);
  if (!rainfallVisible && activeFeature?.properties.display_type === DisplayType.RAINFALL) closeDetailPanel();
});

// ── River level toggle ────────────────────────────────────────────────────────

const riverToggleBtn = document.getElementById("river-toggle")!;
const legendAlertLevel = document.getElementById("legend-alertlevel")!;
let riverVisible = true;

legendAlertLevel.innerHTML =
  `<h3>River alert level</h3>` +
  ([
    [ALERT_COLORS.normal!, "Normal"],
    [ALERT_COLORS.watch!,  "Watch"],
    [ALERT_COLORS.high!,   "High"],
  ] as [string, string][]).map(([color, label]) =>
    `<div class="legend-item"><div class="legend-dot" style="background:${color}"></div>${label}</div>`,
  ).join("");

riverToggleBtn.addEventListener("click", () => {
  riverVisible = !riverVisible;
  const viz = riverVisible ? "visible" : "none";
  if (map.getLayer("river-symbols")) map.setLayoutProperty("river-symbols", "visibility", viz);
  if (map.getLayer("river-polygons")) map.setLayoutProperty("river-polygons", "visibility", viz);
  if (map.getLayer("river-polygon-borders")) map.setLayoutProperty("river-polygon-borders", "visibility", viz);
  setMultiMarkersVisible(rainfallVisible, riverVisible);
  riverToggleBtn.classList.toggle("active", riverVisible);
  legendAlertLevel.classList.toggle("visible", riverVisible);
  if (!riverVisible && activeFeature?.properties.display_type === DisplayType.RIVER) closeDetailPanel();
});

// ── Groundwater toggle ────────────────────────────────────────────────────────

const wellsToggleBtn = document.getElementById("wells-toggle")!;
const legendGroundwater = document.getElementById("legend-groundwater")!;
let wellsVisible = false;

legendGroundwater.innerHTML =
  `<h3>Groundwater</h3>` +
  ([
    [WELL_COLORS.high.icon,    "High"],
    [WELL_COLORS.normal.icon,  "Normal"],
    [WELL_COLORS.low.icon,     "Low"],
    [WELL_COLORS.lowData.icon, "Low data"],
    [WELL_COLORS.stale.icon,   "Stale"],
  ] as [string, string][]).map(([color, label]) =>
    `<div class="legend-item"><div class="legend-dot" style="background:${color}"></div>${label}</div>`,
  ).join("");

wellsToggleBtn.addEventListener("click", () => {
  wellsVisible = !wellsVisible;
  map.setLayoutProperty("well-symbols", "visibility", wellsVisible ? "visible" : "none");
  wellsToggleBtn.classList.toggle("active", wellsVisible);
  legendGroundwater.classList.toggle("visible", wellsVisible);
  if (!wellsVisible && activeFeature?.properties.display_type === DisplayType.WELL) closeDetailPanel();
});

// ── Flood extents toggle ──────────────────────────────────────────────────────

const floodExtentsBtn = document.getElementById("flood-extents-toggle")!;
let floodExtentsVisible = false;
let floodExtentsLoaded = false;

const FLOOD_EXTENTS_URL =
  "https://services1.arcgis.com/hWByVnSkh6ElzHkf/arcgis/rest/services/Flood_Extent_Observations_gdb/FeatureServer/0/query?outFields=*&where=1%3D1&f=geojson";

floodExtentsBtn.addEventListener("click", async () => {
  floodExtentsVisible = !floodExtentsVisible;

  if (!floodExtentsLoaded) {
    floodExtentsBtn.classList.add("loading");
    try {
      const res = await fetch(FLOOD_EXTENTS_URL);
      const geojson = await res.json() as GeoJSON.FeatureCollection;
      addFloodExtentsLayer(map, geojson);
      floodExtentsLoaded = true;

      map.on("click", "flood-extents-fill", (e) => {
        const props = e.features?.[0]?.properties;
        if (!props) return;
        const rows = [
          props.Event ? `<strong>${props.Event}</strong>` : null,
          props.Status ? `Status: ${props.Status}` : null,
          props.ObservationMethod ? `Method: ${props.ObservationMethod}` : null,
        ].filter(Boolean).join("<br>");
        new maplibregl.Popup({ closeButton: false, maxWidth: "240px" })
          .setLngLat(e.lngLat)
          .setHTML(`<span style="color:#111">${rows}</span>`)
          .addTo(map);
      });

      map.on("mouseenter", "flood-extents-fill", () => {
        map.getCanvas().style.cursor = "pointer";
      });
      map.on("mouseleave", "flood-extents-fill", () => {
        map.getCanvas().style.cursor = "";
      });
    } catch (err) {
      log.error("flood extents load failed", { error: String(err) });
      floodExtentsVisible = false;
      floodExtentsBtn.classList.remove("loading");
      return;
    }
    floodExtentsBtn.classList.remove("loading");
  }

  const viz = floodExtentsVisible ? "visible" : "none";
  map.setLayoutProperty("flood-extents-fill", "visibility", viz);
  map.setLayoutProperty("flood-extents-border", "visibility", viz);
  floodExtentsBtn.classList.toggle("active", floodExtentsVisible);
});

// ── Historical mode ───────────────────────────────────────────────────────────

let historicalAt: string | null = null;
let refreshTimer: ReturnType<typeof setInterval> | null = null;
let fp: { setDate(d: Date, triggerChange?: boolean): void } | null = null;
let historyViewBtn: HTMLButtonElement | null = null;

const historyAtInput = document.getElementById("history-at") as HTMLInputElement;
const historyBackBtn = document.getElementById("history-back")!;
const historyNotice = document.getElementById("history-notice")!;
const lastUpdatedEl = document.getElementById("last-updated")!;
const historyModalEl = document.getElementById("history-modal")!;

async function triggerHistoryAt(atNzst: string): Promise<void> {
  // Set historicalAt immediately so any site clicked during the loading wait
  // enters historical mode rather than hitting the live session cache.
  historicalAt = atNzst;
  historyBackBtn.classList.remove("active");
  if (refreshTimer) { clearInterval(refreshTimer); refreshTimer = null; }

  // Immediately re-render the open chart for the new date — don't wait for
  // the slow snapshot fetch (which can take 24–56 s for beyond-30-day dates).
  renderChart();

  // Show loading modal — older dates require a live Hilltop fetch (~15–30 s)
  if (historyViewBtn) historyViewBtn.disabled = true;
  historyModalEl.classList.add("visible");

  const val = atNzst.slice(0, 16); // "YYYY-MM-DDTHH:mm"
  const result: SnapshotAtResult = await getSnapshotAt(atNzst).catch((err) => ({
    ok: false as const,
    beyondWindow: false,
    error: String(err),
  }));

  historyModalEl.classList.remove("visible");
  if (historyViewBtn) historyViewBtn.disabled = false;

  if (result.ok) {
    applySnapshot(result.snapshot);
    lastUpdatedEl.textContent = `Historical: ${val.replace("T", " ")} NZST`;
    historyNotice.className = "";
  } else {
    // Snapshot fetch failed (Hilltop error or future date).
    // Still in historical mode (set above) so per-site charts can be tried individually.
    lastUpdatedEl.textContent = `Historical: ${val.replace("T", " ")} NZST (map unavailable)`;
    historyNotice.textContent = result.error.includes("future")
      ? "Selected time is in the future — showing live data."
      : "Could not load historical map snapshot — click any gauge to view its historical chart.";
    historyNotice.className = "visible";
    log.error("snapshot-at failed", { error: result.error });
  }

  renderChart();
}

void (async () => {
  const [flatpickrMod] = await Promise.all([
    import("flatpickr"),
    import("flatpickr/dist/themes/dark.css"),
  ]);
  const nowNzst = nzstNowForFlatpickr();
  flatpickrMod.default(historyAtInput, {
    enableTime: true,
    time_24hr: true,
    dateFormat: "Y-m-d\\TH:i",    // output format: "YYYY-MM-DDTHH:mm" treated as NZST
    defaultDate: nowNzst,          // pre-populated with current NZST time
    maxDate: nowNzst,              // cannot select future NZST dates
    disableMobile: true,           // use flatpickr on all platforms
    onReady(_dates, _str, fpInstance) {
      fp = fpInstance;
      const btn = document.createElement("button") as HTMLButtonElement;
      btn.id = "history-go";
      btn.textContent = "View";
      fpInstance.calendarContainer.appendChild(btn);
      historyViewBtn = btn;

      btn.addEventListener("click", () => {
        fpInstance.close();
        const val = historyAtInput.value; // "YYYY-MM-DDTHH:mm" — always NZST
        if (!val) return;
        triggerHistoryAt(`${val}:00`).catch((err) => log.error("unhandled error", { error: String(err) }));
      });
    },
  });
})();

document.addEventListener("chart:select-time", (e) => {
  const { at } = (e as CustomEvent<{ at: string }>).detail;
  // Sync the flatpickr display (no-timezone string → parsed as local → displays as NZST)
  if (fp) fp.setDate(new Date(at), false);
  triggerHistoryAt(at).catch((err) => log.error("unhandled error", { error: String(err) }));
});

historyBackBtn.addEventListener("click", () => {
  historicalAt = null;
  historyBackBtn.classList.add("active");
  historyNotice.className = "";
  renderChart();
  loadGauges().catch((err) => log.error("unhandled error", { error: String(err) }));
  refreshTimer = setInterval(() => loadGauges().catch((err) => log.error("unhandled error", { error: String(err) })), 15 * 60 * 1000);
});

// ── Loading overlay ───────────────────────────────────────────────────────────

const loadingOverlay = document.getElementById("loading-overlay")!;
let firstLoadComplete = false;

function hideLoadingOverlay(): void {
  if (firstLoadComplete) return;
  firstLoadComplete = true;
  loadingOverlay.classList.add("hidden");
}

// ── Detail panel ──────────────────────────────────────────────────────────────

const detailPanel = document.getElementById("detail-panel")!;
const detailTitle = document.getElementById("detail-title")!;
const detailClose = document.getElementById("detail-close")!;
const panelCollapse = document.getElementById("panel-collapse")!;
const thresholdToggleBtn = document.getElementById("threshold-toggle")!;
const forecastCompareBtn = document.getElementById("forecast-compare")!;
const timeRangeControls = document.getElementById("time-range-controls")!;
const timeRangeBtns = Array.from(document.querySelectorAll<HTMLButtonElement>("#time-range-controls .time-range-btn"));
const wellTimeControls = document.getElementById("well-time-controls")!;
const wellTimeBtns = Array.from(document.querySelectorAll<HTMLButtonElement>("#well-time-controls .time-range-btn"));
const gaugeCanvas = document.getElementById("gauge-canvas") as HTMLCanvasElement;
const miniCanvas = document.getElementById("gauge-canvas-mini") as HTMLCanvasElement;
const miniCanvasWrap = document.getElementById("chart-mini-wrap")!;

let rainfallTimeRange: "24hr" | "7d" | "14d" | "30d" = "7d";
let riverTimeRange: "24hr" | "7d" | "14d" | "30d" = "7d";
let forecastCompareEnabled = false;

thresholdToggleBtn.addEventListener("click", () => {
  thresholdToggleBtn.classList.toggle("active");
  chartsModule?.toggleHourlyThresholds();
  chartsModule?.toggleRiverThresholds();
});

forecastCompareBtn.addEventListener("click", () => {
  forecastCompareEnabled = !forecastCompareEnabled;
  forecastCompareBtn.classList.toggle("active", forecastCompareEnabled);
  renderChart();
});


for (const btn of timeRangeBtns) {
  btn.addEventListener("click", () => {
    const range = btn.dataset["range"] as "24hr" | "7d" | "14d" | "30d";
    timeRangeBtns.forEach((b) => b.classList.toggle("active", b === btn));
    const isRainfall = activeFeature?.properties.display_type === DisplayType.RAINFALL;
    if (isRainfall) {
      const wasDailyMode = rainfallTimeRange !== "24hr";
      rainfallTimeRange = range;
      if (wasDailyMode && range !== "24hr") {
        chartsModule?.setDailyVisibleDays(range === "7d" ? 7 : range === "14d" ? 14 : 30);
        return;
      }
    } else {
      const wasMultiDay = riverTimeRange !== "24hr";
      riverTimeRange = range;
      if (wasMultiDay && range !== "24hr") {
        chartsModule?.setRiverWindow(range === "7d" ? 7 : range === "14d" ? 14 : 30);
        return;
      }
    }
    renderChart();
  });
}

for (const btn of wellTimeBtns) {
  btn.addEventListener("click", () => {
    const range = btn.dataset["wellRange"] as WellTimeRange;
    wellTimeBtns.forEach((b) => b.classList.toggle("active", b === btn));
    chartsModule?.setWellTimeRange(range);
  });
}

// MapLibre encodes GeoJSON to MVT internally and may strip numeric properties
// that are not referenced in any paint/layout expression. Store thresholds in a
// separate map keyed by site_id|measurement so multi-gauge sites don't bleed thresholds.
const thresholdMap = new Map<string, { watch_level?: number; high_level?: number }>();

let activeFeature: GaugeFeature | null = null;
let lastSnapshot: GaugeFeatureCollection | null = null;

function getTwin(feature: GaugeFeature): GaugeFeature | null {
  if (!feature.properties.multi_gauge || !lastSnapshot) return null;
  return lastSnapshot.features.find(
    (f) => f.properties.site_id === feature.properties.site_id &&
           f.properties.display_type !== feature.properties.display_type,
  ) ?? null;
}

function renderChart(): void {
  if (!activeFeature) return;
  const { site_id, site_name, measurement, unit, display_type } = activeFeature.properties;
  const { watch_level, high_level } = thresholdMap.get(`${site_id}|${measurement}`) ?? {};
  const isRainfall = display_type === DisplayType.RAINFALL;
  const isWell = display_type === DisplayType.WELL;
  // Daily multi-day view only applies to rainfall and only in live mode
  const isDailyMode = isRainfall && rainfallTimeRange !== "24hr" && !historicalAt;
  const isRiverLive = !isRainfall && !isWell && !historicalAt && riverTimeRange !== "24hr";

  miniCanvasWrap.classList.toggle("visible", isDailyMode || isWell || isRiverLive);
  timeRangeControls.classList.toggle("visible", !isWell && !historicalAt);
  forecastCompareBtn.classList.toggle("visible", isRainfall && !historicalAt);

  const [lon, lat] = activeFeature.geometry.coordinates as [number, number];

  loadCharts().then((charts) => {
    if (isWell) {
      charts.destroyHourlyChart();
      charts.destroyDailyChart();
      charts.destroyRiverChart();
      charts.showWellChart(gaugeCanvas, miniCanvas, site_id, measurement, site_name, unit).catch((err) => log.error("unhandled error", { error: String(err) }));
    } else if (isDailyMode) {
      const days = rainfallTimeRange === "7d" ? 7 : rainfallTimeRange === "14d" ? 14 : 30;
      charts.destroyHourlyChart();
      charts.destroyWellChart();
      charts.destroyRiverChart();
      charts.showDailyRainfallChart(gaugeCanvas, miniCanvas, site_id, measurement, site_name, days, lat, lon, forecastCompareEnabled).catch((err) => log.error("unhandled error", { error: String(err) }));
    } else if (isRiverLive) {
      charts.destroyHourlyChart();
      charts.destroyDailyChart();
      charts.destroyWellChart();
      charts.showRiverChart(gaugeCanvas, miniCanvas, site_id, measurement, site_name, unit, watch_level, high_level).catch((err) => log.error("unhandled error", { error: String(err) }));
    } else {
      charts.destroyDailyChart();
      charts.destroyWellChart();
      charts.destroyRiverChart();
      charts.showHourlyChart(gaugeCanvas, site_id, measurement, site_name, unit, historicalAt ?? undefined, watch_level, high_level, isRainfall, isRainfall ? "bar" : "line", lat, lon, forecastCompareEnabled).catch((err) => log.error("unhandled error", { error: String(err) }));
    }
  }).catch((err) => log.error("chart module load failed", { error: String(err) }));
}

function openDetailPanel(feature: GaugeFeature): void {
  const { site_id, site_name, measurement, display_type } = feature.properties;
  const { watch_level, high_level } = thresholdMap.get(`${site_id}|${measurement}`) ?? {};
  activeFeature = feature;
  detailTitle.textContent = site_name;
  detailPanel.classList.remove("hidden");
  document.body.classList.add("panel-open");
  const twin = getTwin(feature);
  setSelectedGauge(map, feature, twin ? () => openDetailPanel(twin) : undefined);

  const isWell = display_type === DisplayType.WELL;
  const hasThresholds = !isWell && (watch_level !== undefined || high_level !== undefined);
  thresholdToggleBtn.classList.toggle("visible", hasThresholds);
  thresholdToggleBtn.classList.add("active");

  wellTimeControls.classList.toggle("visible", isWell);
  wellTimeBtns.forEach((b) => b.classList.toggle("active", b.dataset["wellRange"] === "1yr"));

  // Reset chart state for each new site
  rainfallTimeRange = "7d";
  riverTimeRange = "7d";
  forecastCompareEnabled = false;
  forecastCompareBtn.classList.remove("active");
  timeRangeBtns.forEach((b) => b.classList.toggle("active", b.dataset["range"] === "7d"));

  renderChart();
}

function closeDetailPanel(): void {
  activeFeature = null;
  detailPanel.classList.add("hidden");
  document.body.classList.remove("panel-open");
  thresholdToggleBtn.classList.remove("visible");
  thresholdToggleBtn.classList.add("active");
  forecastCompareBtn.classList.remove("visible", "active");
  forecastCompareEnabled = false;
  timeRangeControls.classList.remove("visible");
  miniCanvasWrap.classList.remove("visible");
  rainfallTimeRange = "7d";
  riverTimeRange = "7d";
  timeRangeBtns.forEach((b) => b.classList.toggle("active", b.dataset["range"] === "7d"));
  wellTimeControls.classList.remove("visible");
  setSelectedGauge(map, null);
  chartsModule?.destroyHourlyChart();
  chartsModule?.destroyDailyChart();
  chartsModule?.destroyWellChart();
  chartsModule?.destroyRiverChart();
}

detailClose.addEventListener("click", closeDetailPanel);
panelCollapse.addEventListener("click", closeDetailPanel);

// ── Gauge data loading ────────────────────────────────────────────────────────

function splitSnapshot(snapshot: GaugeFeatureCollection): {
  rainfall: GaugeFeatureCollection;
  river: GaugeFeatureCollection;
  well: GaugeFeatureCollection;
} {
  const filter = (type: string) => ({
    ...snapshot,
    features: snapshot.features.filter((f) => f.properties.display_type === type),
  });
  return {
    rainfall: filter(DisplayType.RAINFALL),
    river: filter(DisplayType.RIVER),
    well: filter(DisplayType.WELL),
  };
}

let riverPolygons: GeoJSON.FeatureCollection | null = null;

function applySnapshot(snapshot: GaugeFeatureCollection): void {
  lastSnapshot = snapshot;
  // Repopulate threshold lookup from the fresh snapshot before gauge layers are
  // re-added, so openDetailPanel always has current values regardless of what
  // MapLibre's MVT encoding preserves in click-event feature properties.
  thresholdMap.clear();
  for (const f of snapshot.features) {
    const { site_id, measurement, watch_level, high_level } = f.properties;
    if (watch_level !== undefined || high_level !== undefined) {
      thresholdMap.set(`${site_id}|${measurement}`, { watch_level, high_level });
    }
  }

  const { rainfall, river, well } = splitSnapshot(snapshot);
  if (riverPolygons) {
    const colored = colorRiverPolygons(riverPolygons, river);
    addRiverPolygonsLayer(map, colored);
  }
  removeMultiGaugeMarkers();
  addRainfallLayer(map, rainfall, openDetailPanel);
  addRiverLayer(map, river, openDetailPanel);
  addMultiGaugeMarkers(map, rainfall, river, openDetailPanel);
  addWellLayer(map, well, openDetailPanel);
  map.setLayoutProperty("well-symbols", "visibility", wellsVisible ? "visible" : "none");

  if (map.getLayer("rainfall-symbols")) map.setLayoutProperty("rainfall-symbols", "visibility", rainfallVisible ? "visible" : "none");
  if (map.getLayer("rainfall-trends")) map.setLayoutProperty("rainfall-trends", "visibility", rainfallVisible ? "visible" : "none");
  if (map.getLayer("river-symbols")) map.setLayoutProperty("river-symbols", "visibility", riverVisible ? "visible" : "none");
  if (map.getLayer("river-polygons")) map.setLayoutProperty("river-polygons", "visibility", riverVisible ? "visible" : "none");
  if (map.getLayer("river-polygon-borders")) map.setLayoutProperty("river-polygon-borders", "visibility", riverVisible ? "visible" : "none");
  setMultiMarkersVisible(rainfallVisible, riverVisible);

  // Refresh the selected gauge circle with the new snapshot value so the
  // displayed reading updates when historical mode changes dates.
  if (activeFeature) {
    const { site_id, measurement } = activeFeature.properties;
    const updated = snapshot.features.find(
      (f) => f.properties.site_id === site_id && f.properties.measurement === measurement,
    );
    if (updated) {
      activeFeature = updated;
      const twin = getTwin(updated);
      setSelectedGauge(map, updated, twin ? () => openDetailPanel(twin) : undefined);
    }
  }

  updateAlertPanel(rainfall, river);

  lastUpdatedEl.textContent = `Updated ${nzstHHmm()}`;

  hideLoadingOverlay();
}

async function loadGauges(): Promise<void> {
  const snapshot = await getLiveGauges();
  // Update localStorage cache with fresh data from the periodic refresh
  try {
    localStorage.setItem(SNAPSHOT_CACHE_KEY, JSON.stringify({ data: snapshot, cachedAt: Date.now() }));
  } catch { /* ignore */ }
  applySnapshot(snapshot);
}

// ── Load data on map ready ────────────────────────────────────────────────────

map.on("load", async () => {
  await loadIcons(map);
  if (!usingFallback) setupTerrain(map);

  // Fetch static river polygon geometry once — re-used on every data refresh
  try {
    riverPolygons = await getRiverPolygons();
  } catch (err) {
    log.warn("river polygons load failed", { error: String(err) });
  }

  try {
    // Use localStorage cache on initial load to avoid re-fetching if data is fresh.
    // If KV is empty (fresh local dev), the worker auto-runs ingest (~10-15s) — overlay stays visible.
    const snapshot = await getLiveGaugesCached();
    applySnapshot(snapshot);
  } catch (err) {
    log.error("gauge data load failed", { error: String(err) });
    hideLoadingOverlay();
  }

  // Refresh every 15 minutes (paused in historical mode).
  // Bypass cache so periodic refresh always fetches fresh data.
  refreshTimer = setInterval(() => loadGauges().catch((err) => log.error("unhandled error", { error: String(err) })), 15 * 60 * 1000);
});
